import * as React from 'react';
import { useEffect, useState } from 'react';
import { Button, Card, CardContent, Typography, CircularProgress } from '@mui/material';
import styles from './SpecialistList.module.css'; // Import CSS Modules
import { useLocation, useNavigate } from 'react-router-dom';
import { ProfessionalService } from '../../services/ProfessionalService';
import { checkImageURL } from '../../utils/validations';
import { getImageSrc } from '../../utils/imageUtils';
import { toStringArray } from '../../utils/toStringArray';
import image_not_found from '../../assets/image_not_found.png';
import { UserProfilePicture } from '../../services/UserProfilePicture';

// Professional interface for UI
interface Professional {
  id: number;
  name: string;
  specialty: string;
  description: string;
  image: string | null;
  languages: string[];
  location: string;
  price: string;
  specialties: string[];
  availability: {
    dates: number[];
    timeSlots: string[];
  };
}

const SpecialistList: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const specialtyTitle = location.state?.specialtyTitle;
  const [specialists, setSpecialists] = useState<Professional[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profilePictures, setProfilePictures] = useState<{ [key: number]: string | null }>({});

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    setLoading(true);
    setError(null);

    const loadPromise = specialtyTitle
      ? ProfessionalService.getDoctorsBySpeciality(specialtyTitle)
      : ProfessionalService.getDoctorsBySpeciality('');

    loadPromise
      .then(async (data) => {
        const mapped = (Array.isArray(data) ? data : []).map((item: any) => {
          const prof = item.professionnel || {};
          const user = item.utilisateur || {};
          // Prefer user.id, fallback to prof.utilisateur_id
          const utilisateur_id = user.id || prof.utilisateur_id;
          const specialties = toStringArray(prof.specialites);
          const languages = toStringArray(prof.langues);
          const countries = toStringArray(prof.pays_exercice);
          return {
            id: prof.id,
            utilisateur_id,
            name: `Dr. ${user.prenom || ''} ${user.nom || ''}`.trim(),
            specialty: specialties[0] || specialtyTitle || 'General',
            description: prof.sous_specialites || 'Specialist doctor',
            image: prof.photo_url || null,
            languages: languages.length > 0 ? languages : ['Arabic', 'French'],
            location: countries[0] || 'Tunisia',
            price: prof.honoraire_montant && prof.honoraire_montant > 0 ? `${prof.honoraire_montant} ${prof.honoraire_devise || 'TND'}` : 'N/A',
            specialties: specialties.length > 0 ? specialties : (specialtyTitle ? [specialtyTitle] : []),
            availability: {
              dates: prof.availability?.dates || [],
              timeSlots: prof.availability?.timeSlots || [],
            },
          };
        });
        // Fetch profile pictures in parallel
        const pics: { [key: number]: string | null } = {};
        await Promise.all(
          mapped.map(async (spec) => {
            if (spec.utilisateur_id) {
              try {
                const res = await UserProfilePicture.getProfilePicture(spec.utilisateur_id);
                pics[spec.id] = res?.image_base64 || null;
              } catch {
                pics[spec.id] = null;
              }
            } else {
              pics[spec.id] = null;
            }
          })
        );
        setProfilePictures(pics);
        setSpecialists(mapped);
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to load specialists.');
        setLoading(false);
      });
  }, [specialtyTitle]);

  return (
    <div className={styles.specialistListPage}>
      <div className={styles.container}>
        <div className={styles.sectionHeader}>
          <p style={{ color: "#BF4904", fontSize: "1.25rem", fontWeight: "normal" }}>
            {specialtyTitle ? `${specialtyTitle}` : "Our specialists doctors"}
          </p>

          <Typography variant="h2" className={styles.sectionTitle}>
            {'Our specialists doctors'}
          </Typography>
        </div>
        
        {loading ? (
          <div className={styles.specialistLoading}>
            <CircularProgress />
          </div>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <div className={styles.specialistGrid}>
            {specialists.length === 0 ? (
              <div className={styles.noSpecialistsCard}>
                {/* Illustration: person SVG avatar */}
                <span style={{marginBottom: 24}}>
                  <svg width="72" height="72" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="36" cy="36" r="36" fill="#F4F2EE" />
                    <circle cx="36" cy="30" r="14" fill="#B5674F" />
                    <ellipse cx="36" cy="54" rx="20" ry="12" fill="#B5674F" />
                    <ellipse cx="36" cy="54" rx="16" ry="9" fill="#F4F2EE" />
                    <path d="M36 44c-7 0-14 3-14 7v2c0 1.1.9 2 2 2h24c1.1 0 2-.9 2-2v-2c0-4-7-7-14-7z" fill="#B5674F" />
                    <circle cx="36" cy="30" r="8" fill="#F4F2EE" />
                    <ellipse cx="36" cy="38" rx="6" ry="4" fill="#F4F2EE" />
                  </svg>
                </span>
                <Typography variant="h5" className={styles.noSpecialistsTitle}>
                  No doctors available yet
                </Typography>
                <Typography className={styles.noSpecialistsMessage}>
                  {specialtyTitle
                    ? <>We're working to bring top specialists in <b>{specialtyTitle}</b> soon.<br />Please check back later!</>
                    : <>We're working to bring top specialists soon.<br />Please check back later!</>}
                </Typography>
                <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                  <Button
                    className={styles.bookButton}
                    onClick={() => navigate('/teleconsultation')}
                    variant="contained"
                    disableElevation
                    disableRipple
                  >
                    Back to Specialties 
                  </Button>
                </div>
              </div>
            ) : (
              specialists.map((specialist) => (
                <SpecialistCard 
                  key={specialist.id} 
                  specialist={specialist} 
                  profilePictures={profilePictures}
                  styles={styles}
                />
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const SpecialistCard = ({ 
  specialist, 
  profilePictures,
  styles
}: { 
  specialist: Professional & { utilisateur_id?: number }, 
  profilePictures: { [key: number]: string | null },
  styles: { [key: string]: string }
}) => {
  const navigate = useNavigate();
  const isAuthenticated = () =>
    !!(localStorage.getItem('auth_token') || localStorage.getItem('token'))
    && !!localStorage.getItem('userEmail');

  const bookingPath = `/book-doctor/${specialist.id}`;
  const bookingState = {
    professional: { ...specialist, utilisateur_id: specialist.utilisateur_id },
  };

  const handleBookConsultationClick = () => {
    if (!isAuthenticated()) {
      localStorage.setItem(
        'postLoginRedirect',
        JSON.stringify({
          from: 'SpecialistList',
          target: bookingPath,
          professional: bookingState.professional,
          ts: Date.now(),
        }),
      );
      localStorage.setItem('signInMessage', 'Please sign in to book a consultation.');
      navigate('/signIn', {
        replace: true,
        state: { from: { pathname: bookingPath } },
      });
      return;
    }
    navigate(bookingPath, { state: bookingState });
  };

  return (
    <Card className={styles.specialistCard}>
      <CardContent className={styles.specialistCardContent}>
        {profilePictures[specialist.id] ? (
          <img
            className={styles.specialistImage}
            alt={specialist.name}
            src={profilePictures[specialist.id] || undefined}
          />
        ) : (
          <span style={{marginBottom: 20, display: 'block'}}>
            <svg width="240" height="240" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="36" cy="36" r="36" fill="#F4F2EE" />
              <circle cx="36" cy="30" r="14" fill="#B5674F" />
              <ellipse cx="36" cy="54" rx="20" ry="12" fill="#B5674F" />
              <ellipse cx="36" cy="54" rx="16" ry="9" fill="#F4F2EE" />
              <path d="M36 44c-7 0-14 3-14 7v2c0 1.1.9 2 2 2h24c1.1 0 2-.9 2-2v-2c0-4-7-7-14-7z" fill="#B5674F" />
              <circle cx="36" cy="30" r="8" fill="#F4F2EE" />
              <ellipse cx="36" cy="38" rx="6" ry="4" fill="#F4F2EE" />
            </svg>
          </span>
        )}
        <Typography variant="h3" className={styles.specialistName}>
          {specialist.name}
        </Typography>
        <Typography className={styles.specialistSpecialty}>
          {specialist.specialty}
        </Typography>
        <Typography className={styles.specialistDescription}>
          {specialist.description}
        </Typography>
        <Button 
          className={styles.bookButton} 
          onClick={handleBookConsultationClick}
          variant="contained"
          disableElevation
          disableRipple
        >
          Book a consultation ↗
        </Button>
      </CardContent>
    </Card>
  );
};

export default SpecialistList;